/**
 * Launch calendar readings for a leader (roadmap P-1, P-3).
 *
 * `rollUpByQuarter` turns the engine's weekly team-load buckets into one
 * number per team per calendar quarter: hours of supply, hours of demand and
 * the share used. A week counts in the quarter it starts in.
 *
 * `launchAnswer` is the one-sentence answer to "can we launch what we
 * prioritized?" — how many can be built, how many can launch, and which
 * shared team holds back the most of the ones that can be built but not
 * launched.
 *
 * Pure: reads the placements and buckets the page already has.
 */

import type { BucketUtilization, TimePhasedPlacement } from './timePhasedPlanning.types';

export interface QuarterLoad {
  /** "2026-Q4" — sortable. */
  key: string;
  /** "Q4 2026" — for people. */
  label: string;
  supplyHours: number;
  demandHours: number;
  utilizationPct: number;
}

export interface TeamQuarterLoad {
  resourceType: string;
  quarters: QuarterLoad[];
  /** The highest share used in any quarter, for sorting the busiest team first. */
  peakPct: number;
}

function quarterOf(isoDate: string): { key: string; label: string } {
  const [y, m] = isoDate.split('-').map(Number);
  const q = Math.floor((m - 1) / 3) + 1;
  return { key: `${y}-Q${q}`, label: `Q${q} ${y}` };
}

export function rollUpByQuarter(buckets: BucketUtilization[]): {
  quarters: { key: string; label: string }[];
  teams: TeamQuarterLoad[];
} {
  const quarterLabels = new Map<string, string>();
  const byTeam = new Map<string, Map<string, { supply: number; demand: number }>>();
  for (const b of buckets) {
    const { key, label } = quarterOf(b.bucketStart);
    quarterLabels.set(key, label);
    const team = byTeam.get(b.resourceType) ?? new Map();
    const cell = team.get(key) ?? { supply: 0, demand: 0 };
    cell.supply += b.supplyHours;
    cell.demand += b.demandHours;
    team.set(key, cell);
    byTeam.set(b.resourceType, team);
  }

  const quarters = [...quarterLabels.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, label]) => ({ key, label }));

  const teams: TeamQuarterLoad[] = [...byTeam.entries()].map(([resourceType, cells]) => {
    const rows = quarters
      .filter((q) => cells.has(q.key))
      .map((q) => {
        const c = cells.get(q.key)!;
        return {
          key: q.key,
          label: q.label,
          supplyHours: c.supply,
          demandHours: c.demand,
          utilizationPct: c.supply > 0 ? Math.round((c.demand / c.supply) * 100) : c.demand > 0 ? 999 : 0,
        };
      });
    return {
      resourceType,
      quarters: rows,
      peakPct: rows.reduce((m, r) => Math.max(m, r.utilizationPct), 0),
    };
  });
  teams.sort((a, b) => b.peakPct - a.peakPct || a.resourceType.localeCompare(b.resourceType));

  return { quarters, teams };
}

export interface LaunchAnswer {
  total: number;
  canBuild: number;
  canLaunch: number;
  buildNotLaunch: number;
  /** The team named on the most build-but-not-launch placements, if any. */
  holdingTeam: { name: string; count: number } | null;
  /** Total weeks of slip across slipped launches. */
  slipWeeks: number;
  sentence: string;
}

export function launchAnswer(placements: TimePhasedPlacement[]): LaunchAnswer {
  const total = placements.length;
  const canBuild = placements.filter((p) => p.buildable).length;
  const canLaunch = placements.filter((p) => p.launchable).length;
  const held = placements.filter((p) => p.buildable && !p.launchable);
  const slipWeeks = placements.reduce((s, p) => s + (p.slipWeeks ?? 0), 0);

  const teamCounts = new Map<string, number>();
  for (const p of held) {
    for (const t of new Set(p.impactedTeams ?? [])) {
      teamCounts.set(t, (teamCounts.get(t) ?? 0) + 1);
    }
  }
  let holdingTeam: LaunchAnswer['holdingTeam'] = null;
  for (const [name, count] of teamCounts) {
    if (!holdingTeam || count > holdingTeam.count || (count === holdingTeam.count && name < holdingTeam.name)) {
      holdingTeam = { name, count };
    }
  }

  let sentence: string;
  if (total === 0) {
    sentence = 'Nothing is on the launch calendar yet.';
  } else if (canLaunch === total) {
    sentence = `${total === 1 ? 'The one launch' : `All ${total}`} can be built and launched in this horizon.`;
  } else {
    sentence = `${canBuild} of ${total} can be built; ${canLaunch} can launch.`;
    if (held.length > 0) {
      sentence += ` ${held.length} can be built but not launched`;
      sentence += holdingTeam
        ? ` — ${holdingTeam.name} is short on ${holdingTeam.count} of them.`
        : '.';
    }
  }

  return { total, canBuild, canLaunch, buildNotLaunch: held.length, holdingTeam, slipWeeks, sentence };
}
